/**
 * ExpertDetailView — the standalone "all about THIS expert" page (EXPERT-ROOMS.md §18), the SAME three-panel layout as
 * a registry network's ``PackageDetailView`` so the catalog reads alike for both:
 *   • LEFT (nav): the expert's identity (name · version · publisher · summary), then clickable sections —
 *     Teams · Skills · Agents · What it needs · Facts you keep.
 *   • MIDDLE: the overview (summary · goal · this release's notes) or the selected item (a team's responsibilities
 *     and rules, a skill, an agent, the platform needs with sensitivity, the fact kinds).
 *   • RIGHT: the expert's teams as a diagram (Team → Responsibilities), resizable like the network's.
 * Read-only; the host page owns ← Back and the primary action (Subscribe).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MermaidDiagram from '@stepflow/ui-shared/components/MermaidDiagram.jsx';
import MarkdownRenderer from '@stepflow/ui-shared/components/MarkdownRenderer.jsx';
import NetworkAvatar from '@stepflow/ui-shared/components/dashboard/NetworkAvatar.jsx';

const SENS = { privileged: 'bg-rose-50 text-rose-700', confidential: 'bg-amber-50 text-amber-700', public: 'bg-gray-100 text-gray-600' };

function NavItem({ label, active, sub, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-xs ${
        active ? 'bg-blue-50 font-medium text-blue-800' : 'text-gray-700 hover:bg-gray-50'}`}>
      <span className="truncate">{label}</span>
      {sub ? <span className="shrink-0 text-[10px] text-gray-400">{sub}</span> : null}
    </button>
  );
}

function SectionHeader({ children }) {
  return <div className="mb-1 mt-3 px-2.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{children}</div>;
}

/** PURE — a team's tree in the WORKBENCH's style (one row per team, left → right: Team → its Responsibilities), ids
 *  made safe. The full expert uses the server's ``listing.diagram`` (the very diagram the workbench draws). */
export function teamsDiagram(teams = []) {
  const id = (s, i) => `${String(s || 'n').replace(/[^A-Za-z0-9]/g, '') || 'n'}${i}`;
  const esc = (s) => String(s || '').replace(/["[\]{}()<>]/g, ' ').slice(0, 60);
  const lines = ['flowchart LR', '  classDef team fill:#eef2ff,stroke:#6366f1,font-weight:bold'];
  (teams || []).forEach((t, i) => {
    const tid = id(t.name, i);
    lines.push(`  ${tid}["${esc(t.name)}"]:::team`);
    (t.responsibilities || []).forEach((r, j) => {
      const rid = `${tid}r${j}`;
      lines.push(`  ${rid}["${esc(r.title)} · ${(r.rules || []).length} rules"]`);
      lines.push(`  ${tid} --> ${rid}`);
    });
  });
  return lines.length > 2 ? lines.join('\n') : '';
}

export default function ExpertDetailView({ expert }) {
  const x = expert || {};
  const lst = x.listing || {};
  const teams = lst.teams || [];
  const [sel, setSel] = useState(null);     // { kind: 'team'|'skill'|'agent'|'needs'|'facts', id }
  useEffect(() => { setSel(null); }, [x.expertNetworkId]);

  // the same draggable split as the network page (MIDDLE text · RIGHT diagram)
  const [diagramPct, setDiagramPct] = useState(50);
  const [dragging, setDragging] = useState(false);
  const splitRef = useRef(null);
  const onSplitDrag = useCallback((clientX) => {
    const el = splitRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    setDiagramPct(Math.min(75, Math.max(20, ((rect.right - clientX) / rect.width) * 100)));
  }, []);
  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => { e.preventDefault(); onSplitDrag(e.clientX); };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragging, onSplitDrag]);

  const diagram = useMemo(() => (sel?.kind === 'team' ? teamsDiagram(teams.filter((t) => t.name === sel.id))
    : (lst.diagram || teamsDiagram(teams))), [teams, sel, lst.diagram]);
  const team = sel?.kind === 'team' ? teams.find((t) => t.name === sel.id) : null;
  const skill = sel?.kind === 'skill' ? (lst.skills || []).find((s) => (s.id || s.name) === sel.id) : null;
  const agent = sel?.kind === 'agent' ? (lst.agents || []).find((a) => a.id === sel.id) : null;
  const ruleCount = (t) => (t.responsibilities || []).reduce((n, r) => n + (r.rules || []).length, 0);

  return (
    <div className="flex h-full min-h-0" data-testid="expert-detail">
      {/* LEFT — the expert's identity + section nav */}
      <div className="flex w-64 shrink-0 flex-col overflow-y-auto border-r border-gray-100 bg-gray-50/40 py-3">
        <div className="px-3">
          <div className="flex items-start gap-2">
            <NetworkAvatar networkId={x.expertNetworkId} name={x.name} seed={x.expertNetworkId} shape="hexagon" size={28} className="mt-0.5" />
            <div className="min-w-0">
              <button type="button" onClick={() => setSel(null)} data-testid="xd-title" title="Back to overview"
                className="text-left text-sm font-semibold text-gray-900 hover:text-blue-700">{x.name}</button>
              <div className="mt-0.5 text-[10px] text-gray-400">
                {[lst.label || x.label, lst.domain, x.tenantName ? `by ${x.tenantName}` : ''].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>
          {lst.summary ? <p className="mt-2 text-[11px] italic leading-relaxed text-gray-500">{lst.summary}</p> : null}
          <div className="mt-2 rounded-md border border-violet-200 bg-violet-50/60 px-2 py-1 text-[10px] text-violet-900">
            An expert serves you from its own workspace — subscribe, then plug it into any room. Nothing is copied to you.
          </div>
        </div>
        <SectionHeader>Teams ({teams.length})</SectionHeader>
        {teams.map((t) => (
          <div className="px-1.5" key={t.name}>
            <NavItem label={t.name} sub={`${ruleCount(t)} rules`} testid={`xd-team-${t.name}`}
              active={sel?.kind === 'team' && sel.id === t.name} onClick={() => setSel({ kind: 'team', id: t.name })} />
          </div>
        ))}
        <SectionHeader>Skills ({(lst.skills || []).length})</SectionHeader>
        {!(lst.skills || []).length ? <div className="px-3 text-[11px] text-gray-400">None</div> : null}
        {(lst.skills || []).map((s) => (
          <div className="px-1.5" key={s.id || s.name}>
            <NavItem label={s.name || s.id} testid={`xd-skill-${s.id || s.name}`}
              active={sel?.kind === 'skill' && sel.id === (s.id || s.name)} onClick={() => setSel({ kind: 'skill', id: s.id || s.name })} />
          </div>
        ))}
        <SectionHeader>Agents ({(lst.agents || []).length})</SectionHeader>
        {(lst.agents || []).map((a) => (
          <div className="px-1.5" key={a.id}>
            <NavItem label={a.id} testid={`xd-agent-${a.id}`}
              active={sel?.kind === 'agent' && sel.id === a.id} onClick={() => setSel({ kind: 'agent', id: a.id })} />
          </div>
        ))}
        <SectionHeader>Your workspace</SectionHeader>
        <div className="px-1.5">
          <NavItem label="What it needs" sub={String((lst.needs || []).length)} testid="xd-needs"
            active={sel?.kind === 'needs'} onClick={() => setSel({ kind: 'needs' })} />
          <NavItem label="Facts you keep" sub={String(Object.keys(lst.facts || {}).length)} testid="xd-facts"
            active={sel?.kind === 'facts'} onClick={() => setSel({ kind: 'facts' })} />
        </div>
      </div>

      <div ref={splitRef} className="flex min-w-0 flex-1">
        {/* MIDDLE — overview or the selected item */}
        <div className="min-w-0 flex-1 overflow-y-auto px-5 py-4 text-xs text-gray-800" data-testid="xd-middle">
          {!sel ? (
            <div className="space-y-3" data-testid="xd-overview">
              <h3 className="text-sm font-semibold text-gray-900">Overview</h3>
              {lst.summary ? <p className="leading-relaxed text-gray-700">{lst.summary}</p> : <p className="text-gray-400">No description provided.</p>}
              {lst.goal ? <p className="leading-relaxed text-gray-700"><span className="font-semibold text-gray-500">Goal: </span>{lst.goal}</p> : null}
              {lst.notes ? (
                <div className="rounded-md bg-gray-50 p-2 ring-1 ring-gray-200">
                  <div className="font-semibold text-gray-500">This release{lst.label ? ` (${lst.label})` : ''}</div>
                  <p className="mt-1 whitespace-pre-wrap text-gray-700">{lst.notes}</p>
                </div>
              ) : null}
              <p className="text-gray-600">{teams.length} teams · {lst.ruleCount || 0} rules
                {(lst.procedures || []).length ? ` · ${lst.procedures.length} written procedures` : ''}</p>
              {(lst.tags || []).length ? (
                <div className="flex flex-wrap gap-1">{lst.tags.map((t) => (
                  <span key={t} className="rounded bg-gray-100 px-1.5 py-px text-[10px] text-gray-600">{t}</span>))}</div>
              ) : null}
              <p className="pt-1 text-[11px] text-gray-400">Select a team, skill, agent or your workspace's part on the left.</p>
            </div>
          ) : team ? (
            <div className="space-y-3" data-testid="xd-team">
              <h3 className="text-sm font-semibold text-gray-900">{team.name}</h3>
              {team.criterion ? <p className="text-gray-600"><span className="font-semibold text-gray-500">Guarantees: </span>{team.criterion}</p> : null}
              {(team.responsibilities || []).map((r) => (
                <div key={r.title}>
                  <div className="font-semibold text-gray-700">{r.title}</div>
                  <ul className="ml-4 mt-1 list-disc space-y-0.5 text-gray-700">
                    {(r.rules || []).map((rule, i) => (
                      <li key={i}><span className="mr-1 text-[9px] font-semibold uppercase text-gray-400">{rule.severity}</span>{rule.statement}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : skill ? (
            <div className="space-y-2" data-testid="xd-skill"><h3 className="text-sm font-semibold text-gray-900">{skill.name || skill.id}</h3>
              <div className="text-[11px] uppercase tracking-wide text-gray-400">Skill</div>
              {skill.brief ? <MarkdownRenderer content={skill.brief} />
                : skill.summary ? <p>{skill.summary}</p> : <p className="text-gray-400">No summary.</p>}</div>
          ) : agent ? (
            <div className="space-y-2"><h3 className="text-sm font-semibold text-gray-900">{agent.id}</h3>
              <div className="text-[11px] uppercase tracking-wide text-gray-400">Agent</div>
              {agent.summary ? <p>{agent.summary}</p> : <p className="text-gray-400">No summary.</p>}</div>
          ) : sel.kind === 'needs' ? (
            <div className="space-y-2" data-testid="xd-needs-detail">
              <h3 className="text-sm font-semibold text-gray-900">What it needs from your workspace</h3>
              {(lst.needs || []).length ? (
                <ul className="space-y-1">{lst.needs.map((n, i) => (
                  <li key={i}><span className={`mr-1 rounded px-1 py-px text-[10px] ${SENS[n.sensitivity] || SENS.public}`}>{n.sensitivity}</span>
                    <span className="font-medium">{n.capability}</span>{n.tier ? ` (${n.tier})` : ''}{n.why ? ` — ${n.why}` : ''}</li>))}</ul>
              ) : <p className="text-gray-400">Nothing beyond the platform's basics.</p>}
              <p className="text-[11px] text-gray-500">Your connections and credentials stay in your workspace — the expert never sees them.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="xd-facts-detail">
              <h3 className="text-sm font-semibold text-gray-900">Facts you keep for it (in your workspace)</h3>
              {Object.keys(lst.facts || {}).length ? (
                <ul className="space-y-1">{Object.entries(lst.facts).map(([k, v]) => (
                  <li key={k}><span className="font-medium">{k}</span> — {(v.fields || []).join(', ')}</li>))}</ul>
              ) : <p className="text-gray-400">None.</p>}
              <p className="text-[11px] text-gray-500">Kinds and fields only — your values stay in your workspace.</p>
            </div>
          )}
        </div>
        <div role="separator" aria-orientation="vertical" aria-label="Resize diagram panel" data-testid="xd-split-handle"
          onMouseDown={(e) => { e.preventDefault(); setDragging(true); }} onDoubleClick={() => setDiagramPct(40)}
          title="Drag to resize · double-click to reset"
          className={`hidden w-1.5 shrink-0 cursor-col-resize border-l border-gray-100 transition-colors lg:block ${dragging ? 'bg-blue-300' : 'bg-gray-100 hover:bg-blue-200'}`} />
        <aside className="hidden shrink-0 overflow-auto p-3 lg:block" style={{ width: `${diagramPct}%` }} data-testid="xd-diagram">
          {diagram ? <MermaidDiagram code={diagram} /> : (
            <div className="flex h-full items-center justify-center text-xs text-gray-400">No teams to draw.</div>
          )}
        </aside>
      </div>
    </div>
  );
}
